import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { 
  Bell, Sparkles, MessageSquare, Info, ShieldAlert, 
  CheckSquare, Send, CalendarClock, Volume2, HelpCircle 
} from "lucide-react";
import { 
  fetchNotifications, markNotificationAsRead, 
  markAllNotificationsAsRead, broadcastNotification 
} from "../api/client";
import { format } from "date-fns";

export default function Notifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  
  // Admin Broadcast form state
  const [campaign, setCampaign] = useState({ title: "", category: "info", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadNotifications = () => {
    setLoading(true);
    fetchNotifications()
      .then((data) => {
        setNotifications(data.notifications || []);
      })
      .catch((err) => {
        showToast(err.response?.data?.detail || "Failed to load notifications history", "error");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleRead = async (id) => {
    try {
      await markNotificationAsRead(id);
      // Soft refresh (update item local status instead of full loader)
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      showToast("All notifications marked as read");
    } catch {
      showToast("Failed to mark all notifications as read", "error");
    }
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    if (!campaign.title.trim() || !campaign.message.trim()) {
      showToast("Please enter a title and message", "error");
      return;
    }
    setSubmitting(true);
    try {
      const data = await broadcastNotification({
        title: campaign.title.trim(),
        category: campaign.category.trim(),
        message: campaign.message.trim()
      });
      showToast(data.message || "Announcement broadcasted successfully!");
      setCampaign({ title: "", category: "info", message: "" });
      loadNotifications();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to broadcast announcement", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryTag = (category) => {
    let styles = { background: "rgba(255,255,255,0.06)", color: "var(--text2)", border: "1px solid rgba(255,255,255,0.12)" };
    let label = category?.toUpperCase() || "ALERT";
    
    switch (category?.toLowerCase()) {
      case "offers":
        styles = { background: "rgba(167, 139, 250, 0.08)", color: "var(--accent-purple)", border: "1px solid rgba(167, 139, 250, 0.25)" };
        label = "🔥 OFFER";
        break;
      case "plans":
        styles = { background: "rgba(239, 68, 68, 0.08)", color: "var(--red)", border: "1px solid rgba(239, 68, 68, 0.25)" };
        label = "⚠️ PLAN ALERT";
        break;
      case "announcements":
        styles = { background: "rgba(59, 130, 246, 0.08)", color: "var(--accent)", border: "1px solid rgba(59, 130, 246, 0.25)" };
        label = "📣 ANNOUNCEMENT";
        break;
      case "support":
        styles = { background: "rgba(16, 185, 129, 0.08)", color: "var(--green)", border: "1px solid rgba(16, 185, 129, 0.25)" };
        label = "🎫 TICKET REPLY";
        break;
      default:
        styles = { background: "rgba(0, 242, 254, 0.08)", color: "var(--accent)", border: "1px solid rgba(0, 242, 254, 0.25)" };
        label = "ℹ️ INFO";
    }

    return (
      <span style={{ 
        fontSize: "10px", 
        fontWeight: "800", 
        padding: "3px 8px", 
        borderRadius: "12px", 
        letterSpacing: "0.5px",
        ...styles
      }}>
        {label}
      </span>
    );
  };

  const getCategoryIcon = (category, type) => {
    const defaultColor = "var(--accent)";
    switch (category?.toLowerCase()) {
      case "offers":
        return <Sparkles size={16} style={{ color: "var(--accent-purple)" }} />;
      case "support":
      case "ticket_reply":
        return <MessageSquare size={16} style={{ color: "var(--green)" }} />;
      case "plans":
        return <ShieldAlert size={16} style={{ color: "var(--red)" }} />;
      default:
        return <Info size={16} style={{ color: defaultColor }} />;
    }
  };

  const renderDate = (val) => {
    if (!val) return "—";
    const date = new Date(val);
    return Number.isNaN(date.getTime()) ? "—" : format(date, "dd MMM yyyy, hh:mm a");
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div style={{ paddingBottom: "24px" }}>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      
      <div className="page-header">
        <div>
          <div className="page-title">Notification Hub</div>
          <div className="page-sub">View system alerts, promotional updates, and support ticket replies</div>
        </div>
        {unreadCount > 0 && (
          <button 
            className="btn btn-ghost" 
            onClick={handleMarkAllRead} 
            style={{ gap: "8px", minHeight: "auto", padding: "8px 16px" }}
          >
            <CheckSquare size={15} />
            Mark All Read
          </button>
        )}
      </div>

      <div className={user?.role === "admin" ? "grid-2" : "single-col"} style={{ display: "grid", gridTemplateColumns: user?.role === "admin" ? "1.9fr 1.1fr" : "1fr", gap: "24px" }}>
        
        {/* ── LEFT COLUMN: NOTIFICATION LOG TIMELINE ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div className="card" style={{ padding: 24, marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, borderBottom: "1px solid var(--border)", paddingBottom: "12px" }}>
              <Bell size={20} style={{ color: "var(--accent)" }} />
              <div style={{ fontWeight: 700, fontSize: 16 }}>Broadcast &amp; Message Log</div>
              <span style={{ fontSize: "12px", color: "var(--text2)", marginLeft: "auto" }}>
                Unread: {unreadCount}
              </span>
            </div>

            {loading ? (
              <div className="loading">Loading alerts database...</div>
            ) : notifications.length === 0 ? (
              <div className="empty" style={{ padding: "40px 0" }}>No notification logs found.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => !notif.is_read && handleRead(notif.id)}
                    style={{
                      display: "flex",
                      gap: "16px",
                      background: notif.is_read ? "rgba(0,0,0,0.12)" : "rgba(0, 242, 254, 0.02)",
                      border: notif.is_read ? "1px solid var(--border)" : "1px solid rgba(0, 242, 254, 0.15)",
                      borderRadius: "var(--radius)",
                      padding: "16px 20px",
                      transition: "all 0.2s ease",
                      cursor: notif.is_read ? "default" : "pointer",
                      boxShadow: notif.is_read ? "none" : "0 0 10px rgba(0,242,254,0.03)"
                    }}
                    onMouseEnter={(e) => {
                      if (!notif.is_read) e.currentTarget.style.borderColor = "var(--accent)";
                    }}
                    onMouseLeave={(e) => {
                      if (!notif.is_read) e.currentTarget.style.borderColor = "rgba(0, 242, 254, 0.15)";
                    }}
                  >
                    <div style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.03)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0
                    }}>
                      {getCategoryIcon(notif.category || (notif.type === "personal" ? "support" : "info"))}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "8px", marginBottom: "6px" }}>
                        <span style={{ fontWeight: 800, fontSize: "14px", color: notif.is_read ? "var(--text2)" : "var(--text)" }}>
                          {notif.title}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {getCategoryTag(notif.category || (notif.type === "personal" ? "support" : "info"))}
                          {!notif.is_read && (
                            <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "var(--accent)", boxShadow: "0 0 8px var(--accent)" }} />
                          )}
                        </div>
                      </div>

                      <p style={{ 
                        fontSize: "13px", 
                        color: notif.is_read ? "var(--text2)" : "var(--text)", 
                        lineHeight: "1.5", 
                        marginBottom: "12px",
                        whiteSpace: "pre-line"
                      }}>
                        {notif.message}
                      </p>

                      <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--text2)", fontSize: "11px" }}>
                        <CalendarClock size={12} />
                        <span>{renderDate(notif.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT COLUMN: ADMIN BROADCAST CREATOR (ADMIN ONLY) ── */}
        {user?.role === "admin" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <form className="card" style={{ padding: 24, marginBottom: 0 }} onSubmit={handleBroadcast}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, borderBottom: "1px solid var(--border)", paddingBottom: "12px" }}>
                <Send size={20} style={{ color: "var(--accent)" }} />
                <div style={{ fontWeight: 700, fontSize: 16 }}>Create Broadcast Campaign</div>
              </div>

              <p style={{ color: "var(--text2)", fontSize: "12px", marginBottom: "16px", lineHeight: "1.5" }}>
                Send global announcements, seasonal subscription discounts, notifications of plan additions, or general maintenance alerts to all platform clients instantly.
              </p>

              <div className="form-group">
                <label className="form-label">Announcement Title</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="e.g. Server Maintenance Notice"
                  value={campaign.title}
                  onChange={e => setCampaign(p => ({ ...p, title: e.target.value }))}
                  disabled={submitting}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Campaign Type</label>
                <select
                  className="form-input"
                  value={campaign.category}
                  onChange={e => setCampaign(p => ({ ...p, category: e.target.value }))}
                  disabled={submitting}
                  style={{ background: "var(--bg-dark)", color: "var(--text)", border: "1px solid var(--border)", outline: "none", cursor: "pointer" }}
                >
                  <option value="info">General Information Alert</option>
                  <option value="offers">Vibrant Promotion &amp; Offers</option>
                  <option value="plans">Important Subscriptions/Plans Update</option>
                  <option value="announcements">System Announcements</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Notification Body Description</label>
                <textarea
                  className="form-input"
                  placeholder="Draft the details of your notification campaign..."
                  value={campaign.message}
                  onChange={e => setCampaign(p => ({ ...p, message: e.target.value }))}
                  disabled={submitting}
                  style={{ minHeight: "120px", resize: "vertical", lineHeight: "1.5" }}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: "100%", justifyContent: "center", gap: "8px" }}
                disabled={submitting}
              >
                <Send size={16} />
                {submitting ? "Broadcasting..." : "Publish Broadcast"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
